import { motion } from "motion/react";
import { useEffect, useId, useRef, useState } from "react";

import { cn } from "@/lib/utils";

/** Paper tones for the envelope: a real white letter, deliberately identical on every theme. */
const PAPER = "#ffffff";
const PAPER_SHADE = "#edf1f6";
const PAPER_EDGE = "#dde3ea";
const BADGE = "#d6dce4";

const HERO_HEIGHT = 236;

/** A four-point sparkle star with concave sides, the classic "shine" glyph. */
const star = (x: number, y: number, s: number): string => {
  const q = s / 4.5;
  return [
    `M ${x} ${y - s}`,
    `C ${x} ${y - q}, ${x + q} ${y}, ${x + s} ${y}`,
    `C ${x + q} ${y}, ${x} ${y + q}, ${x} ${y + s}`,
    `C ${x} ${y + q}, ${x - q} ${y}, ${x - s} ${y}`,
    `C ${x - q} ${y}, ${x} ${y - q}, ${x} ${y - s} Z`,
  ].join(" ");
};

export interface NewsletterHeroProps {
  /** Shows the confirmation seal on the envelope's fold. */
  subscribed?: boolean;
  /**
   * Bump this (e.g. with Date.now()) when a subscribe just succeeded: the envelope does a happy
   * hop and a burst of sparkles fires around the seal. Leave at 0 for a calm render.
   */
  celebrate?: number;
  className?: string;
}

/** Sparkle burst positions around the envelope, in envelope-local coordinates. */
const BURST = [
  { x: 96, y: 78, s: 7, delay: 0 },
  { x: 246, y: 70, s: 6, delay: 0.06 },
  { x: 86, y: 168, s: 5, delay: 0.12 },
  { x: 254, y: 160, s: 6, delay: 0.09 },
  { x: 170, y: 40, s: 8, delay: 0.03 },
  { x: 226, y: 208, s: 5, delay: 0.15 },
];

/**
 * The newsletter artwork, shared look with the mobile signup sheet: a panel washed in the brand
 * color, saturated at the top and fading out at the bottom, broad soft arcs ringing down from
 * above, white sparkle stars, and a large open envelope holding a single letter. One SVG driven
 * by `currentColor`, so it repaints with the theme; the host width is measured so the drawing
 * stays centered and undistorted at any size.
 */
export function NewsletterHero({ subscribed = false, celebrate = 0, className }: NewsletterHeroProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const uid = useId().replace(/[^a-zA-Z0-9]/g, "");
  const bgId = `nl-bg-${uid}`;
  const arcId = `nl-arc-${uid}`;
  const arcLineId = `nl-arcline-${uid}`;
  const foldId = `nl-fold-${uid}`;

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const observer = new ResizeObserver(() => {
      setWidth((current) => (current === host.clientWidth ? current : host.clientWidth));
    });
    observer.observe(host);
    setWidth(host.clientWidth);
    return () => observer.disconnect();
  }, []);

  // Everything below draws around this axis, so the composition re-centers on any panel width.
  const cx = width / 2;

  return (
    <div ref={hostRef} className={cn("text-primary h-[236px] w-full overflow-hidden", className)}>
      {width > 0 ? (
        <svg width={width} height={HERO_HEIGHT} viewBox={`0 0 ${width} ${HERO_HEIGHT}`} aria-hidden="true">
          <defs>
            <linearGradient id={bgId} x1="0" y1="0" x2="0" y2={HERO_HEIGHT} gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="currentColor" stopOpacity={0.95} />
              <stop offset="0.45" stopColor="currentColor" stopOpacity={0.5} />
              <stop offset="0.8" stopColor="currentColor" stopOpacity={0.12} />
              <stop offset="1" stopColor="currentColor" stopOpacity={0} />
            </linearGradient>
            <linearGradient id={arcId} x1="0" y1="0" x2="0" y2="160" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#ffffff" stopOpacity={0.18} />
              <stop offset="1" stopColor="#ffffff" stopOpacity={0} />
            </linearGradient>
            <linearGradient id={arcLineId} x1="0" y1="0" x2="0" y2="150" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor="#ffffff" stopOpacity={0.4} />
              <stop offset="1" stopColor="#ffffff" stopOpacity={0} />
            </linearGradient>
            <linearGradient id={foldId} x1="0" y1="128" x2="0" y2="210" gradientUnits="userSpaceOnUse">
              <stop offset="0" stopColor={PAPER} />
              <stop offset="1" stopColor={PAPER_SHADE} />
            </linearGradient>
          </defs>

          {/* The brand wash, saturated at the top and gone by the bottom edge. */}
          <rect x={0} y={0} width={width} height={HERO_HEIGHT} fill={`url(#${bgId})`} />

          {/* Broad soft arcs ringing out from above, plus two hairline ripples. */}
          <circle cx={cx} cy={-70} r={125} fill="none" stroke={`url(#${arcId})`} strokeWidth={34} />
          <circle cx={cx} cy={-70} r={185} fill="none" stroke={`url(#${arcId})`} strokeWidth={30} />
          <circle cx={cx} cy={-70} r={243} fill="none" stroke={`url(#${arcId})`} strokeWidth={26} />
          <circle cx={cx} cy={-70} r={152} fill="none" stroke={`url(#${arcLineId})`} strokeWidth={1.4} />
          <circle cx={cx} cy={-70} r={212} fill="none" stroke={`url(#${arcLineId})`} strokeWidth={1.4} />

          {/* Sparkles in the saturated sky. */}
          <path d={star(cx - 96, 54, 7)} fill="#ffffff" opacity={0.95} />
          <path d={star(cx + 90, 40, 5)} fill="#ffffff" opacity={0.85} />
          <path d={star(cx + 116, 92, 4)} fill="#ffffff" opacity={0.6} />
          <path d={star(cx - 124, 98, 3.5)} fill="#ffffff" opacity={0.5} />
          <circle cx={cx - 60} cy={30} r={1.8} fill="#ffffff" opacity={0.7} />
          <circle cx={cx + 48} cy={70} r={1.5} fill="#ffffff" opacity={0.55} />

          {/* The envelope, centered and slightly enlarged; a successful subscribe gives it a hop. */}
          <motion.g
            key={celebrate || undefined}
            initial={false}
            animate={celebrate ? { y: [0, -10, 0, -3, 0] } : { y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
          <g transform={`translate(${cx}, 150) scale(1.16) translate(-170, -150)`}>
            {/* Soft landing shadow. */}
            <ellipse cx={170} cy={216} rx={86} ry={8} fill="#000000" opacity={0.1} />

            {/* Open top flap behind the letter. */}
            <path d="M 102 134 L 163 68 Q 170 61 177 68 L 238 134 Z" fill={PAPER} stroke={PAPER_EDGE} strokeWidth={1.4} />
            <path d="M 102 134 L 163 68 Q 170 61 177 68 L 238 134 Z" fill="#000000" opacity={0.04} />

            {/* One letter rising out: badge dot and brand-tinted address lines. */}
            <rect x={114} y={96} width={112} height={92} rx={8} fill={PAPER} stroke={PAPER_EDGE} strokeWidth={1.4} />
            <circle cx={136} cy={118} r={9} fill={BADGE} />
            <rect x={128} y={138} width={84} height={4.5} rx={2.25} fill="currentColor" opacity={0.5} />
            <rect x={128} y={149} width={84} height={4.5} rx={2.25} fill="currentColor" opacity={0.38} />
            <rect x={128} y={160} width={58} height={4.5} rx={2.25} fill="currentColor" opacity={0.28} />

            {/* Envelope front: the pocket with its inward V fold. */}
            <path
              d="M 100 134 L 165 182 Q 170 185.5 175 182 L 240 134 L 240 198 Q 240 210 228 210 L 112 210 Q 100 210 100 198 Z"
              fill={`url(#${foldId})`}
              stroke={PAPER_EDGE}
              strokeWidth={1.4}
            />
            <path d="M 100 134 L 165 182 Q 170 185.5 175 182 L 240 134" fill="none" stroke={PAPER_EDGE} strokeWidth={1.4} />

            {/* Confirmation seal on the fold's point once subscribed; it springs in on success. */}
            {subscribed ? (
              <motion.g
                initial={celebrate ? { scale: 0 } : false}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 420, damping: 16, delay: celebrate ? 0.18 : 0 }}
                style={{ transformBox: "fill-box", transformOrigin: "center" }}
              >
                <circle cx={170} cy={182} r={13} fill="currentColor" />
                <circle cx={170} cy={182} r={13} fill="#000000" opacity={0.08} />
                <path
                  d="M 163.5 182 L 168 186.5 L 177 177"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth={2.6}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </motion.g>
            ) : null}

            {/* One-shot sparkle burst around the envelope when a subscribe lands. */}
            {celebrate
              ? BURST.map((spark) => (
                  <motion.path
                    key={`${celebrate}-${spark.x}-${spark.y}`}
                    d={star(spark.x, spark.y, spark.s)}
                    fill="currentColor"
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: [0, 1.25, 1, 0], opacity: [0, 1, 1, 0] }}
                    transition={{ duration: 1.1, delay: spark.delay, ease: "easeOut", times: [0, 0.3, 0.6, 1] }}
                    style={{ transformBox: "fill-box", transformOrigin: "center" }}
                  />
                ))
              : null}
          </g>
          </motion.g>
        </svg>
      ) : null}
    </div>
  );
}
